<template>
  <div class="edit-post-form">
    <div v-if="isLoadingSummaryPayload" class="edit-post-form__loading-overlay">
      <Loader class="edit-post-form__loader" />
    </div>
    <!-- Standard page header -->
    <div class="standard-page-header edit-post-form__header">
      <h1 class="global-h1 standard-page-header__title">Customize &amp; Use this Post</h1>
      <div class="standard-page-header__subtitle-and-link">
        <div class="standard-page-header__subtitle">Easily share this post on your preferred social channels!</div>
      </div>
    </div>
    <!-- / Standard page header -->

    <div class="edit-post-form__section">
      <h4 class="global-h4 edit-post-form__section-title">Caption</h4>
      <!-- Caption edit form -->
      <div class="caption-edit-form edit-post-form__section-content">
        <div v-if="props.postToEdit?.caption" class="caption-edit-form__caption-text" v-html="DOMPurify.sanitize(props.postToEdit.caption)"></div>

        <div class="caption-edit-form__buttons-panel">
          <SrpButton class="caption-edit-form__button" size="small" @click="copyCapton" :isDisabled="isRequestingDownload || props.isSavingPost">
            <template #icon>
              <IconEmbedded v-if="isShowCopied" name="check_2-5" :size="16" />
              <IconEmbedded v-else name="copy_2-5" :size="16" />
            </template>
            {{ isShowCopied ? "Copied" : "Copy" }}
          </SrpButton>

          <SrpButton
            class="media-thumbnails-section__button"
            size="small"
            fill="outlined"
            @click="emit('clickEditCaption')"
            :isDisabled="isRequestingDownload || props.isSavingPost || props.readonlyMode"
          >
            <template #icon><IconEmbedded name="pencil_2" :size="18" /></template>
            Edit Caption
          </SrpButton>
        </div>
      </div>
      <!-- / Caption edit form -->
    </div>

    <div class="edit-post-form__section">
      <h4 class="global-h4 edit-post-form__section-title">
        Photos & Videos<span style="color: rgba(0, 0, 0, 0.5)"> <!--{{ props.postToEdit.assetIds.length }}--></span>
      </h4>
      <!-- Media thumbnails section -->
      <div class="media-thumbnails-section edit-post-form__section-content">
        <div
          :class="{
            'media-thumbnails-section__thumbnails-list-wrap': true,
            'media-thumbnails-section__thumbnails-list-wrap--with-left-curtain': isLeftCurtainVisible,
            'media-thumbnails-section__thumbnails-list-wrap--with-right-curtain': isRightCurtainVisible,
          }"
        >
          <ul class="media-thumbnails-section__thumbnails-list" ref="domRefThumbnailsList">
            <SrpFileThumbnail
              class="media-thumbnails-section__thumbnail"
              v-for="assetId in props.postToEdit.assetIds"
              :key="assetId"
              thumbSize="thumb-tiny"
              :fileName="assetId"
              :isFixedAspectRatio="false"
              :gallery-postfix="props.postToEdit.id"
              isEntireAreaClickable
            />
          </ul>
        </div>

        <div class="media-thumbnails-section__buttons-panel">
          <SrpButton class="media-thumbnails-section__button" size="small" @click="handleDownloadClick" :isDisabled="isRequestingDownload || props.isSavingPost">
            <template #icon>
              <LoadingIcon v-if="isRequestingDownload || props.isSavingPost" :size="18" />
              <IconEmbedded v-else name="download_2-5" :size="18" />
            </template>
            Download All
          </SrpButton>

          <SrpButton class="media-thumbnails-section__button" size="small" fill="outlined" @click="emit('clickEditImages')" :isDisabled="isRequestingDownload || props.isSavingPost">
            <template #icon><IconEmbedded name="pencil_2" :size="18" /></template>
            Edit Images
          </SrpButton>
        </div>
      </div>
      <!-- / Media thumbnails section -->
    </div>

    <div v-if="!props.sectionsToHide.includes(SocialPostEditAndShareFormSections.CreatorTag) && socialShareSummaryPayload?.adventureCreatorSummaries?.length" class="edit-post-form__section">
      <h4 class="global-h4 edit-post-form__section-title">Creator tag</h4>

      <!-- Tags copying section -->
      <div class="tags-copying-section edit-post-form__section-content" v-for="creatorSummary in socialShareSummaryPayload.adventureCreatorSummaries" :key="creatorSummary.creatorId">
        <div class="tags-copying-section__left-side">
          <AvatarWithFallback class="tags-copying-section__avatar" :src="creatorSummary.profilePhoto" />
        </div>
        <div class="tags-copying-section__right-side">
          <h5 class="global-h5 tags-copying-section__title">
            {{ creatorSummary.displayName }}
          </h5>
          <SocialMediaLinksBar class="tags-copying-section__handles-row" show-copy-icon :isButtonHaveText="false" :socialFields="creatorSummary.socialFields" />
        </div>
      </div>
      <!-- / Tags copying section -->
    </div>

    <div v-if="false" class="edit-post-form__section">
      <h4 class="global-h4 edit-post-form__section-title">Locations</h4>
      <!-- Tags copying section -->
      <div class="tags-copying-section edit-post-form__section-content">
        <div class="tags-copying-section__left-side">
          <IconEmbedded class="tags-copying-section__location-icon" name="map-pin_1-5" :size="45" />
        </div>
        <div class="tags-copying-section__right-side">
          <h5 class="global-h5 tags-copying-section__title">McDonalds</h5>
          <SocialMediaLinksBar
            class="tags-copying-section__handles-row"
            show-copy-icon
            :isButtonHaveText="false"
            :socialFields="{
              twitterHandle: 'kotofeyweb',
              instagramHandle: '',
              facebookURL: '',
              youtubeURL: 'https://youtube.com/kotofey_web',
              tikTokHandle: '',
            }"
          />
        </div>
      </div>
      <!-- / Tags copying section -->
    </div>

    <div v-if="!props.sectionsToHide.includes(SocialPostEditAndShareFormSections.WhereWasItUsed)" class="edit-post-form__section">
      <h4 class="global-h4 edit-post-form__section-title">
        Where was it used?
        <!-- <span style="color: rgba(0, 0, 0, 0.5); font-weight: 500">(optional)</span>-->
        <InfoIconWithTooltip icon="trending-up_2-5" :width="300"
          >Let us know the url of the post.<br />We're laying the groundwork to track performance so we can help you see what's working!
        </InfoIconWithTooltip>
      </h4>
      <div style="margin-bottom: 7px">Providing a link to the post helps us see how successful it is.</div>

      <div class="edit-post-form__section-content">
        <input class="global-text-input" :disabled="props.isSavingPost" v-model="props.postToEdit.postUrl" style="max-width: 500px" placeholder="https://facebook.com/example" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import { MetricSender } from "@helpers/MetricSender";
import FileDownload from "@logic/FileDownload";
import DOMPurify from "dompurify";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import SocialMediaLinksBar from "@components/SocialMediaLinksBar.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import Loader from "@components/Loader/Loader.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";

// Composables
import useScrollCurtains from "@composables/useScrollCurtains";

// Types
import { SocialMediaPostData, SocialShareSummaryPayload } from "@contracts/shareStop";
import { SocialPostEditAndShareFormSections } from "./index.vue";

// Global variables & injections
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;

const props = withDefaults(
  defineProps<{
    postToEdit: SocialMediaPostData | null;
    customerId: string;
    isLoadingSummaryPayload: boolean;
    socialShareSummaryPayload: SocialShareSummaryPayload | null;
    savePostCallback: Function;
    isSavingPost: boolean;
    readonlyMode?: boolean;
    sectionsToHide?: Array<SocialPostEditAndShareFormSections>;
  }>(),
  {
    postToEdit: null,
    customerId: "",
    isLoadingSummaryPayload: false,
    socialShareSummaryPayload: null,
    savePostCallback: () => {},
    isSavingPost: false,
    readonlyMode: false,
    sectionsToHide: () => [],
  }
);

const emit = defineEmits<{
  (e: "clickEditImages"): void;
  (e: "clickEditCaption"): void;
  (e: "requestDownload", value: boolean): void;
}>();

// Creators list curtains =====================================================
const domRefThumbnailsList = ref<HTMLElement | null>(null);
const { isLeft: isLeftCurtainVisible, isRight: isRightCurtainVisible } = useScrollCurtains(domRefThumbnailsList);

// Request download ===========================================================
const isRequestingDownload = ref<boolean>(false);

async function handleDownloadClick() {
  await props.savePostCallback();
  await requestDownload();
  MetricSender.sendMetric("Cust-SocialPost-Download", { socialPostId: props.postToEdit.id });
}

async function requestDownload() {
  isRequestingDownload.value = true;
  emit("requestDownload", true);

  const downloadId = await ShareStopSummaryRepo.requestDownloadId(props.customerId, props.postToEdit.id);

  isRequestingDownload.value = false;
  emit("requestDownload", false);

  FileDownload.downloadFileByURI(`${contentBaseUri}/${downloadId}`);
}

// Copy caption ===============================================================
const isShowCopied = ref<boolean>(false);

function copyCapton() {
  const text = props.postToEdit.caption;

  navigator.clipboard.writeText(text).then(
    () => console.info("Copied to clipboard: " + text),
    err => console.error("Failed to copy to clipboard: ", err)
  );

  isShowCopied.value = true;
  setTimeout(() => (isShowCopied.value = false), 3000);
}
</script>

<style scoped lang="scss">
// Caption edit form ==========================================================
.caption-edit-form {
  &__caption-text {
    margin-bottom: 13px;
    white-space: pre-wrap;
  }

  &__buttons-panel {
    gap: 12px;
    display: flex;
  }

  &__button {
  }
}

// Media thumbnails section ===================================================
.media-thumbnails-section {
  &__thumbnails-list-wrap {
    position: relative;
    z-index: 0;

    &::before,
    &::after {
      content: "";
      width: 30px;
      height: calc(100% - 5px);
      position: absolute;
      top: 0;
      z-index: 3;
      opacity: 0;
      background: rgba(255, 0, 0, 0.3);
      transition: opacity 0.07s ease-in-out;
      pointer-events: none;
    }

    &::before {
      left: 0;
      background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
    }
    &::after {
      right: 0;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    }

    &--with-left-curtain {
      &::before {
        opacity: 1;
      }
    }
    &--with-right-curtain {
      &::after {
        opacity: 1;
      }
    }
  }

  &__thumbnails-list {
    gap: 10px;
    padding: 0 0 10px;
    margin: 0 0 12px;
    display: flex;
    list-style: none;
    position: relative;
    overflow-y: hidden;
    overflow-x: scroll;
    scrollbar-width: thin; /* Firefox */
    scrollbar-gutter: stable;
    scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
    scroll-behavior: smooth;

    &::-webkit-scrollbar {
      width: 7px !important;
      height: 7px !important;
    }

    &::-webkit-scrollbar-track {
      background: transparent !important;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 100px;
      background-color: rgba(0, 0, 0, 0.25);
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: rgba(0, 0, 0, 0.35);
    }
  }

  &__thumbnail {
    height: 80px;

    :deep(.file-thumbnail__bg-image) {
      width: auto !important;
    }
  }

  &__buttons-panel {
    gap: 12px;
    display: flex;
  }

  &__button {
  }
}

// Tags copying section =======================================================
.tags-copying-section {
  padding: 14px 12px 14px;
  border-radius: 6px;
  display: flex;
  background: rgba(0, 0, 0, 0.05);

  &__left-side {
  }

  &__avatar {
    width: 42px !important;
    margin-right: 10px;
  }

  &__location-icon {
    margin-right: 8px;
  }

  &__right-side {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__title {
    margin-bottom: 5px;
    font-weight: 500;
  }

  &__handles-row {
    padding-left: 6px;
    display: flex;

    :deep(.social-links__in-wrap) {
      justify-content: flex-start;
    }
  }
}

// Edit post form =============================================================
.edit-post-form {
  position: relative;
  z-index: 0;

  &__loading-overlay {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    z-index: 1;
    background: rgba(255, 255, 255, 0.7);
  }

  &__loader {
    position: fixed;
    inset: calc(50dvh - 100px) auto auto calc(50% - 15px);
  }

  &__header {
    padding-bottom: 20px;
    margin-bottom: 20px;
  }

  &__section {
    padding-bottom: 25px;
    margin-bottom: 20px;
    position: relative;

    &::after {
      content: "";
      width: 100%;
      height: 0;
      border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
      position: absolute;
      inset: auto auto 0 0;
    }

    &:last-child {
      padding-bottom: 0;
      margin-bottom: 0;

      &:after {
        display: none;
      }
    }
  }

  &__section-title {
    margin-bottom: 7px;
  }

  &__section-content {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__divider {
  }
}
</style>
